import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Pagination from '../../component/Pagination'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'
import {
    applyAppGifticonCompanyOrderDraft,
    clearAppGifticonCompanyOrderDraft,
    createAppGifticonCompanyOrderDraftPayload,
    createEditAppGifticonCompanyOrderDraftKey,
    readAppGifticonCompanyOrderDraft,
    saveAppGifticonCompanyOrderDraft,
} from './appGifticonCompanyOrderDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    paymentId: '',
    orderId: '',
    payMethod: '',
    regTime: '',
    transferLimitTime: '',
    transferUsername: '',
    cashNo: '',
    cashEmail: '',
    totalAmount: 0,
    payStatus: '',
    desc: '',
})

export default function AppGifticonCompanyOrder(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.paymentId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppGifticonCompanyOrderDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [orderItems, setOrderItems] = useState([])
    const [curPage, setCurPage] = useState(1)
    const offset = 5
    const [totalCount, setTotalCount] = useState(0)
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar || '')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 무통장입금 주문 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setOrderItems([])
                setTotalCount(0)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/gifticonOrder/getDetail', { page: curPage, offset: offset, paymentId: id })
                if (data.code === 200) {
                    const detailData = data.data || {}
                    const orderInfo = detailData.orderInfo || {}

                    if (!orderInfo.paymentId) {
                        setParams(createDefaultParams())
                        setOrderItems([])
                        setTotalCount(0)
                        setDetailErrorMessage('무통장입금 주문 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppGifticonCompanyOrderDraft(draftKey)
                    const restoredDraft = applyAppGifticonCompanyOrderDraft({
                        baseParams: orderInfo,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppGifticonCompanyOrderDraftPayload({
                        params: orderInfo,
                    }))

                    setTotalCount(detailData.count || 0)
                    setOrderItems(detailData.rows || [])
                    setParams(restoredDraft.params)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '무통장입금 주문 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setOrderItems([])
                    setTotalCount(0)
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '무통장입금 주문 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setOrderItems([])
                setTotalCount(0)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [curPage, draftKey, history, id, offset])

    useEffect(() => {
        if (!draftReady || !params?.paymentId) {
            return
        }

        // 무통장입금 주문 상세는 목록 query를 건드리지 않고 주문상태 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppGifticonCompanyOrderDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppGifticonCompanyOrderDraft(draftKey)
            return
        }

        saveAppGifticonCompanyOrderDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 저장 후에도 원래 목록 query와 필터를 그대로 유지한 채 복귀한다.
    const moveToList = (snackbarState) => {
        clearAppGifticonCompanyOrderDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appGifticonCompanyOrders',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/gifticonOrder/update', { paymentId: params.paymentId, payStatus: params.payStatus, desc: params.desc })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: '무통장입금 주문 상태를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('무통장입금 주문 상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="무통장입금 주문 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.paymentId) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>무통장입금 주문 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>무통장입금 주문 상세</Title>
                <Paper sx={{ p: 2, }}>
                    <EmbeddedTableContainer>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>브랜드명</TableCell>
                                <TableCell>상품명</TableCell>
                                <TableCell>주문수량</TableCell>
                                <TableCell>주문금액</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orderItems.map((row) => (
                            <TableRow key={row.orderItemId}>
                                <TableCell>{row.brandName}</TableCell>
                                <TableCell>{row.goodsName}</TableCell>
                                <TableCell>{row.quantity}</TableCell>
                                <TableCell>{formatNumber(row.totalAmount)}</TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </EmbeddedTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                </Paper>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="주문번호" secondary={params.orderId || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="결제수단" secondary={params.payMethod === 'trans' ? '무통장입금' : '카드'} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="주문일시" secondary={params.regTime || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="입금기한" secondary={params.transferLimitTime || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="총주문금액" secondary={formatNumber(params.totalAmount)} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="입금자명" secondary={params.transferUsername || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="현금영수증 사업자번호" secondary={params.cashNo || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="현금영수증 이메일" secondary={params.cashEmail || ''} />
                            </ListItem>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    select
                                    label="주문상태"
                                    id="payStatus"
                                    name="payStatus"
                                    value={params.payStatus}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                >
                                    <MenuItem value={'ready'}>입금대기</MenuItem>
                                    <MenuItem value={'paid'}>결제완료</MenuItem>
                                    <MenuItem value={'failed'}>입금전취소</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="메모"
                                    multiline
                                    rows={7}
                                    name="desc"
                                    value={params.desc || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
